import React, { FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import { getShortUrl } from '../../../services/calculationsApi';
import { useReactToPrint } from 'react-to-print';
import { FormContext } from '../../../store/context/formContext';
import { numberToCurrency } from '../../../utils/prices';
import { Button } from '../../shared/Button';
import { Text } from '../../shared/Text';
import { getWhatsAppShareText } from '../../../services/shareHelper';
import ReactDOM from 'react-dom';
import OverviewModal from '../../modals/OverviewModal';
// import SharePdfModal from '../../modals/SharePdfModal';
import { assetPrefix } from '../../../utils/assetPrefix';
import { dotsToComma } from '../../../services/helpers';

export interface MaxLoanCalculatorInformation {
    maximumMortgageForMonthlyPayment: number;
}

export const MaxLoanCalculatorInformation: FunctionComponent<{
    data: MaxLoanCalculatorInformation | null;
}> = ({ data }) => {
    const { hasServerError, form, nhg, selectedInterest, selectedPeriod, calculatedMortgageType } =
        useContext(FormContext);
    const [isOverviewOpened, setIsOverviewOpened] = useState(false);
    const [isCalculated, setIsCalculated] = useState(false);

    const componentRef: any = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    useEffect(() => {
        if (data?.maximumMortgageForMonthlyPayment !== undefined) {
            data?.maximumMortgageForMonthlyPayment > 0
                ? setIsCalculated(true)
                : setIsCalculated(false);
        }
    }, [data]);

    const toggleOverview = () => {
        setIsOverviewOpened(!isOverviewOpened);
    };

    const print = () => {
        toggleOverview();

        setTimeout(() => {
            handlePrint();
        }, 200);
    };

    const shareWhatsApp = async () => {
        const url = 'url';
        const { hostname } = window.location;
        const state = {
            monthlyCost: form?.monthlyCost,
            maximumMortgageForMonthlyPayment: data?.maximumMortgageForMonthlyPayment,
        };

        if (hostname.indexOf('localhost') === -1) {
            const shortUrl = await getShortUrl(url, '&utm_source=berekening&utm_medium=whatsapp');
            const shareText = getWhatsAppShareText(state, shortUrl.link);
            window.open(`https://api.whatsapp.com/send?text=${shareText}`);
        } else {
            console.log(url);
            console.log(getWhatsAppShareText(state, window.location));
        }
    };

    return (
        <div className="c-max-loan-calculator-information">
            <Text as="h1">Hoeveel wil je lenen?</Text>

            {hasServerError ? (
                <div className="form__error">
                    <Text as="h3">
                        Er is een onverwacht probleem opgetreden, probeer het later opnieuw.
                    </Text>
                </div>
            ) : (
                <>
                    <div className="u-flex u-flex-between u-flex-middle">
                        <div className="u-h3">Maximale hypotheek</div>
                        <div className="u-h2">
                            {numberToCurrency(data?.maximumMortgageForMonthlyPayment || 0, true)}
                        </div>
                    </div>
                    {data?.maximumMortgageForMonthlyPayment && nhg &&
                        data?.maximumMortgageForMonthlyPayment > nhg.existingBuilding &&
                        form?.isNhg === 'true' && (
                            <div className="form__error">
                                <Text as="p">
                                    Je hebt gekozen voor een hypotheek met NHG. Op basis van deze
                                    maandlasten komt het hypotheekbedrag echter boven de NHG grens
                                    ({numberToCurrency(nhg?.existingBuilding, true)}) uit. Bereken opnieuw zonder NHG.
                                </Text>
                            </div>
                        )}
                </>
            )}
            <hr />

            <div className="share-buttons">
                <div className="share-print-and-pdf-buttons">
                    <button
                        className="share-button share-button--icon"
                        disabled={!isCalculated}
                        onClick={print}>
                        <img
                            src={`${assetPrefix}/assets/svg/${
                                !isCalculated ? 'grey/' : ''
                            }print.svg`}
                            alt=""
                        />
                    </button>

                    <button
                        className="share-button share-button--icon"
                        disabled={!isCalculated}
                        onClick={shareWhatsApp}>
                        <img
                            src={`${assetPrefix}/assets/svg/${
                                !isCalculated ? 'grey/' : ''
                            }whatsapp.svg`}
                            alt=""
                        />
                    </button>
                </div>

                <button
                    className="share-button share-button--calculation"
                    disabled={!isCalculated || !data}
                    onClick={toggleOverview}>
                    Toon volledige berekening
                </button>
            </div>

            <hr />
            {isCalculated && (
                <div>
                    <h2 className="u-h3">Over deze berekening</h2>
                    <p>
                        De maximale hypotheek is gebaseerd op de door jou ingevoerde gewenste bruto
                        maandlasten, de laagste hypotheekrente op dit moment{' '}
                        {dotsToComma(selectedInterest)}%, (
                        {calculatedMortgageType === 4
                            ? 'Annuïteitenhypotheek'
                            : 'Lineaire hypotheek'}
                        , {selectedPeriod} jaar vast). Maak gebruik van onze maximale hypotheek
                        berekening om een goed beeld te krijgen van je daadwerkelijke leencapaciteit
                        op basis van je inkomen en eventuele andere financiële verplichtingen.
                    </p>
                    <Button
                        url="https://www.hypotheker.nl/zelf-berekenen/hoeveel-kan-ik-lenen/"
                        label="Maximale hypotheek berekenen"
                        variant="tertiary"
                    />
                    <hr />
                </div>
            )}

            <h2 className="u-h3">Afspraak maken met onze hypotheekadviseur?</h2>
            <p>
                Meer weten over de mogelijkheden voor jouw persoonlijke situatie? Maak een afspraak
                bij een van onze onafhankelijke hypotheekadviseurs. Het eerste gesprek is op onze
                kosten.
            </p>
            <Button
                url="https://www.hypotheker.nl/afspraak-maken-hypotheekadviseur/"
                label="Afspraak maken"
                variant="tertiary"
            />

            {isOverviewOpened
                ? ReactDOM.createPortal(
                      <OverviewModal toggleOverview={toggleOverview} componentRef={componentRef} />,
                      (document as any).getElementById('overview-modal-portal'),
                  )
                : null}
        </div>
    );
};
